/**
 * <InfiniteList> — presentational paginated / infinite-scroll list (molecule, L0.1).
 *
 * Owns scroll, pull-to-refresh, end-reached, empty state, and the load-more footer — la forme
 * des éléments qui arrivent (`moreSkeleton`), jamais une roue (plus a footer error + retry for a
 * failed next page — the one state the page shell can't render, since the list is non-empty).
 * The parent owns the page cursor and fetch — no Redux, no API. Pair with
 * `<PageTemplate scroll={false}>` so this FlatList is the only scroller.
 *
 * Un rafraîchissement, lui, ne montre rien dans la liste : les lignes sont déjà là. Seul le
 * tirer-pour-rafraîchir natif tourne, et il dit un GESTE : la roue ne tourne qu'après un tirer (`usePulledRefresh`),
 * même quand `refreshing` dit une relecture partie d'ailleurs.
 *
 * Distinct from `<List>` (titled section skeleton for short stacks). Compose atoms only.
 */
import React, { useCallback } from "react"
import { FlatList, View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { usePulledRefresh } from "../../hooks/usePulledRefresh"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { dsScrollProps } from "../../utils/webScrollbars"

export type InfiniteListProps<T> = {
    data: T[]
    renderItem: (item: T, index: number) => React.ReactElement | null
    keyExtractor: (item: T, index: number) => string
    /** Shown when `data` is empty (and not refreshing). */
    emptyMessage?: string
    /** La relecture de la première page : le tirer-pour-rafraîchir natif tourne, rien d'autre. */
    refreshing?: boolean
    onRefresh?: () => void
    /** La page suivante est en route : le pied montre `moreSkeleton`. */
    loadingMore?: boolean
    /**
     * La forme des éléments qui arrivent — un ou deux squelettes de ligne, ceux de `renderItem`.
     * Posée au pied pendant `loadingMore`, après le seuil d'apparition, sous son propre balayage.
     */
    moreSkeleton?: React.ReactNode
    /**
     * Error for a page fetched *after* the first one. The page shell can't render this —
     * the list is non-empty — so it surfaces here as a footer banner + retry.
     */
    loadMoreError?: string | null
    onRetryLoadMore?: () => void
    /** When false, `onEndReached` is ignored. Defaults to false. */
    hasMore?: boolean
    onEndReached?: () => void
    onEndReachedThreshold?: number
    /**
     * Total items available (API `meta.total`). When set and `data` is non-empty,
     * the footer shows `{loaded} / {total}` (legacy PaginatedFlatList pattern).
     */
    totalCount?: number
    /** Optional fixed header above the rows (inside the list). */
    header?: React.ReactElement
    /** Vertical gap between rows — spacing token, default `"4"`. */
    gap?: SpacingKey
    contentContainerStyle?: StyleProp<ViewStyle>
    testID?: string
}

export function InfiniteList<T>(props: InfiniteListProps<T>) {
    const {
        data,
        renderItem,
        keyExtractor,
        emptyMessage,
        refreshing = false,
        onRefresh,
        loadingMore = false,
        moreSkeleton,
        loadMoreError,
        onRetryLoadMore,
        hasMore = false,
        onEndReached,
        onEndReachedThreshold = 0.2,
        totalCount,
        header,
        gap = "4",
        contentContainerStyle,
        testID,
    } = props
    const pull = usePulledRefresh(refreshing, onRefresh)

    const handleEndReached = useCallback(() => {
        // A failed page stops auto-fetching — otherwise scrolling at the bottom would
        // retry in a loop. The footer's explicit retry is the way out.
        if (!hasMore || loadingMore || refreshing || loadMoreError) return
        onEndReached?.()
    }, [hasMore, loadingMore, refreshing, loadMoreError, onEndReached])

    const showCount = data.length > 0 && totalCount != null && totalCount > 0

    return (
        <FlatList
            {...dsScrollProps()}
            testID={testID}
            style={styles.flex}
            data={data}
            keyExtractor={keyExtractor}
            renderItem={({ item, index }) => renderItem(item, index)}
            ItemSeparatorComponent={() => <View style={styles.separator(gap)} />}
            ListHeaderComponent={header}
            ListEmptyComponent={
                !refreshing && emptyMessage != null ? (
                    <Text color="text-muted" center style={styles.empty}>
                        {emptyMessage}
                    </Text>
                ) : null
            }
            ListFooterComponent={
                <View style={styles.footer}>
                    {loadingMore && moreSkeleton != null && (
                        <DelayedSkeleton style={styles.more(gap)}>{moreSkeleton}</DelayedSkeleton>
                    )}
                    {!loadingMore && !!loadMoreError && (
                        <>
                            <Banner message={loadMoreError} color="danger" icon="exclamation-circle" tinted />
                            {onRetryLoadMore != null && (
                                <Button label="Réessayer" size="sm" variant="outline" onPress={onRetryLoadMore} />
                            )}
                        </>
                    )}
                    {showCount && (
                        <Text size="text-xs" weight="light" color="text-muted" center>
                            {data.length} / {totalCount}
                        </Text>
                    )}
                </View>
            }
            refreshing={pull.refreshing}
            onRefresh={pull.onRefresh}
            onEndReached={handleEndReached}
            onEndReachedThreshold={onEndReachedThreshold}
            contentContainerStyle={[styles.content, data.length === 0 && styles.contentEmpty, contentContainerStyle]}
            showsVerticalScrollIndicator={false}
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    flex: {
        flex: 1,
    },
    content: {
        flexGrow: 1,
        paddingHorizontal: theme.spacing["3"],
        paddingTop: theme.spacing["3"],
        paddingBottom: theme.spacing["6"],
    },
    contentEmpty: {
        justifyContent: "center" as const,
    },
    separator: (gap: SpacingKey) => ({
        height: theme.spacing[gap],
    }),
    empty: {
        paddingVertical: theme.spacing["8"],
    },
    /** Les éléments qui arrivent prennent la largeur de la liste, et son écart. */
    more: (gap: SpacingKey) => ({
        alignSelf: "stretch" as const,
        gap: theme.spacing[gap],
    }),
    footer: {
        alignItems: "center" as const,
        gap: theme.spacing["2"],
        paddingVertical: theme.spacing["4"],
        paddingBottom: theme.spacing["8"],
    },
}))
